import { type ForwardedRef, forwardRef } from 'react';
import { useTranslation } from 'next-i18next/pages';
import {
  getListingPrice,
  resolveListingDataValue,
  type ExtraPriceInfo,
  type ListingPriceAdditionalInfo,
} from '@pkgs/components/utils/listingDataUtils';
import { Skeleton } from '@pkgs/components/components/Skeleton/Skeleton';
import { type UserAccountExclusiveListing, type Listing, type RestrictedListing, PropertyMarketingType } from '@pkgs/api';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Copy } from '@ev-tech/ui-core-components/Copy';

import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useSettings } from '@/hooks/useSettings';
import { useListing } from '@/hooks/queries/useListing';

import { PropertyPriceExtraInfoTooltip } from '../PropertyPriceExtraInfoTooltip/PropertyPriceExtraInfoTooltip';

import styles from './PropertyPriceLabel.module.css';

function isAdditionalPriceInfo(info: ExtraPriceInfo): info is ListingPriceAdditionalInfo {
  return info.fieldName === 'propertyTax' || info.fieldName === 'commissionPercent' || info.fieldName === 'sellerCommission';
}

const ListingPropertyPriceLabel = forwardRef<HTMLDivElement, { listing: Listing | UserAccountExclusiveListing }>(
  ({ listing }, ref: ForwardedRef<HTMLDivElement>) => {
    const { t: tListingData } = useTranslation(TRANSLATION_NAMESPACE.listing_data);
    const { isFetching } = useListing();
    const { currency, language } = useSettings();
    const priceDescriptor = getListingPrice(listing, { currency, language, returnExtraPriceInfo: true });
    const { convertedValue, labelKey } = priceDescriptor;
    const price = resolveListingDataValue(priceDescriptor, tListingData);
    const convertedPrice = convertedValue;
    const label = tListingData(labelKey);
    const extraPriceInfo = 'extraPriceInfo' in priceDescriptor ? priceDescriptor.extraPriceInfo : [];
    const additionalPriceInfo = extraPriceInfo.filter(isAdditionalPriceInfo);
    const priceDetails = extraPriceInfo.filter((info) => !isAdditionalPriceInfo(info));
    const showPriceLabel =
      !!label &&
      (listing.propertyMarketingType === PropertyMarketingType.rent ||
        priceDetails.length > 0 ||
        ('isDevelopmentProject' in listing && listing.isDevelopmentProject && Boolean(price)));

    return (
      <div data-testid="expose-price-info" ref={ref}>
        <div className={styles.additionalInfoContainer}>
          {showPriceLabel && <Copy className={styles.additionalInfoCopy} text={label} />}
          {priceDetails.length > 0 && <PropertyPriceExtraInfoTooltip extraPriceInfo={priceDetails} />}
        </div>
        {!!price && (
          <div className={styles.priceLabelContainer}>
            <Headline label={price} size="h4" as="span" />

            {convertedPrice && (
              <Skeleton loading={isFetching}>
                <Headline label={convertedPrice} size="h5" as="span" />
              </Skeleton>
            )}
          </div>
        )}
        {additionalPriceInfo.map((info) => (
          <Copy
            className={styles.priceInfoCopy}
            key={info.fieldName}
            text={
              info.fieldName === 'sellerCommission'
                ? tListingData(info.labelKey)
                : tListingData(info.labelKey, { [info.fieldName]: info.value })
            }
          />
        ))}
      </div>
    );
  },
);

ListingPropertyPriceLabel.displayName = 'ListingPropertyPriceLabel';

const RestrictedPropertyPriceLabel = forwardRef<HTMLDivElement, { restrictedListing: RestrictedListing }>(
  ({ restrictedListing }, ref: ForwardedRef<HTMLDivElement>) => {
    const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
    return (
      <div data-testid="expose-price-info" ref={ref}>
        <div className={styles.priceLabelContainer} data-restricted={Boolean(restrictedListing)}>
          <Headline label={t('search.map.priceOnRequest')} size="h4" as="span" />
          <Headline label={t('search.map.priceOnRequest')} size="h5" as="span" />
        </div>
      </div>
    );
  },
);

RestrictedPropertyPriceLabel.displayName = 'RestrictedPropertyPriceLabel';

export const PropertyPriceLabel = forwardRef<HTMLDivElement>((_, ref) => {
  const {
    data: { listing, restrictedListing, userAccountExclusiveListing },
  } = useListing();

  if (listing) {
    return <ListingPropertyPriceLabel ref={ref} listing={listing} />;
  }

  if (restrictedListing) {
    return <RestrictedPropertyPriceLabel ref={ref} restrictedListing={restrictedListing} />;
  }
  if (userAccountExclusiveListing) {
    return <ListingPropertyPriceLabel ref={ref} listing={userAccountExclusiveListing} />;
  }
  return null;
});

PropertyPriceLabel.displayName = 'PropertyPriceLabel';
